// AI管理コンソール — モデル管理 / 機能ルーティング（Sheet編集）
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Settings2, SlidersHorizontal, Gauge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { API } from './AiConsole';
import { DocReadCard } from './DocReadCard';
import { jstDateTime } from '@/utils/jstDate';

const RUNTIME_API = `${process.env.REACT_APP_BACKEND_URL}/api/ai/runtime`;

const CAP = {
  text: { label: 'TEXT', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  vision: { label: 'VISION', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  embedding: { label: 'EMBEDDING', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
export const CapBadge = ({ type }) => (
  <span className={`rounded border px-1.5 py-0.5 text-[9px] font-bold ${(CAP[type] || CAP.text).cls}`}>
    {(CAP[type] || CAP.text).label}
  </span>
);

export const Card = ({ title, hint, children, testid, action }) => (
  <div className="space-y-4 rounded-xl border border-zinc-200/60 bg-white p-6 shadow-sm" data-testid={testid}>
    <div className="flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold tracking-tight text-zinc-900">{title}</h2>
        {hint && <p className="mt-1 text-[11.5px] leading-relaxed text-zinc-500">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

// ── モデル管理 ────────────────────────────────────────────────────────────────
export function ModelsSection({ models, onModels, isCustom }) {
  const [busy, setBusy] = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/models?refresh=1`, { withCredentials: true });
      onModels(r.data.models || []);
      toast.success(`${(r.data.models || []).filter((m) => m.online).length} 件のモデルを検出しました`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'モデル検出に失敗しました'); } finally { setBusy(false); }
  };
  // Default view hides offline entries — the admin explicitly opts in to see
  // them (matches Feb 2026 user request).
  const filtered = showOffline ? models : models.filter((m) => m.online !== false);
  const hiddenCount = models.length - filtered.length;
  return (
    <Card title="検出されたモデル" testid="ai-console-models"
      hint="LM Studio から自動検出します。手入力は不要です — サーバーでモデルを追加したら「再検出」を押すだけです。デフォルトではオンラインのモデルのみ表示します。"
      action={
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer" title="オフラインのモデルも一覧に表示">
            <input type="checkbox" checked={showOffline} onChange={(e) => setShowOffline(e.target.checked)}
              data-testid="ai-console-models-show-offline" className="h-3 w-3 accent-indigo-600" />
            オフラインを表示
          </label>
          <Button size="sm" onClick={refresh} disabled={busy || !isCustom}
            className="h-9 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-console-models-refresh">
            <RefreshCw size={13} strokeWidth={1.5} className={`mr-1.5 ${busy ? 'animate-spin' : ''}`} />
            {busy ? '検出中…' : 'モデルを再検出'}
          </Button>
        </div>
      }>
      {!isCustom && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-700">
          ローカルLLMプロバイダーが無効です。「概要と接続」でカスタムに切り替えると検出できます。
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-zinc-200/60">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-[10px] text-zinc-500">
            <tr>
              {['モデル', '種別', 'コンテキスト長', '状態', '最終確認'].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-t border-zinc-200 transition-colors duration-200 hover:bg-teal-50/50"
                data-testid={`ai-console-model-${m.id}`}>
                <td className="px-3 py-2 font-mono font-semibold text-zinc-800">{m.id}</td>
                <td className="px-3 py-2"><CapBadge type={m.type} /></td>
                <td className="px-3 py-2 tabular-nums text-zinc-600">{m.context_length ? m.context_length.toLocaleString() : '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold ${m.online === false ? 'text-zinc-400' : m.state === 'loaded' ? 'text-emerald-600' : 'text-zinc-500'}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${m.online === false ? 'bg-zinc-300' : m.state === 'loaded' ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                    {m.online === false ? 'オフライン' : m.state === 'loaded' ? 'ロード済み' : '未ロード'}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-400">{jstDateTime(m.last_seen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="px-3 py-8 text-center text-xs text-zinc-400" data-testid="ai-console-models-empty">
            {models.length === 0
              ? 'モデルが未検出です — 右上の「モデルを再検出」を押してください'
              : 'オンラインのモデルがありません — LM Studioでモデルをロードしてください'}
          </p>
        )}
        {hiddenCount > 0 && filtered.length > 0 && (
          <p className="px-3 py-2 text-center text-[10px] text-zinc-400 border-t border-zinc-200">
            {hiddenCount}件のオフラインモデルは非表示（右上のチェックで表示できます）
          </p>
        )}
      </div>
    </Card>
  );
}

// ── 推論パラメータグリッド（Sheet内・詳細設定用） ─────────────────────────────
const PARAM_DEFS = [
  ['temperature', '温度', '0.7'], ['max_tokens', '最大出力トークン', '2048'],
  ['top_p', 'Top P', '0.9'], ['top_k', 'Top K', '40'], ['min_p', 'Min P', '—'],
  ['repeat_penalty', '繰り返しペナルティ', '1.1'], ['seed', 'シード値', '—'],
];
const ADV_DEFS = [
  ['presence_penalty', '出現ペナルティ', '—'], ['frequency_penalty', '頻度ペナルティ', '—'],
  ['repeat_last_n', '繰り返し判定範囲', '—'], ['typical_p', 'Typical P', '—'],
  ['tfs_z', 'TFS Z', '—'], ['mirostat_tau', 'Mirostat Tau', '—'], ['mirostat_eta', 'Mirostat Eta', '—'],
];
const NumField = ({ k, label, ph, values, onChange, prefix }) => (
  <div className="min-w-0">
    <label className="mb-0.5 block truncate text-[10px] font-semibold leading-tight text-zinc-500" title={label}>{label}</label>
    <Input type="number" step="any" value={values[k] ?? ''} placeholder={ph}
      onChange={(e) => onChange(k, e.target.value === '' ? null : Number(e.target.value))}
      className="h-8 bg-white text-xs" data-testid={`${prefix}-${k}`} />
  </div>
);
const SelField = ({ k, label, opts, values, onChange, prefix }) => (
  <div className="min-w-0">
    <label className="mb-0.5 block truncate text-[10px] font-semibold leading-tight text-zinc-500" title={label}>{label}</label>
    <Select value={values[k] != null && values[k] !== '' ? String(values[k]) : '__inherit__'}
      onValueChange={(v) => onChange(k, v === '__inherit__' ? null : (k === 'mirostat' ? Number(v) : v))}>
      <SelectTrigger className="h-8 bg-white text-xs" data-testid={`${prefix}-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">
        <SelectItem value="__inherit__">継承</SelectItem>
        {opts.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);
// 設定プリセット — 適用後も個別編集可。プリセット名ではなく実値を保存する
const PRESETS = [
  ['正確', { temperature: 0.1, top_p: 0.9, top_k: 40, seed: 42, max_tokens: 2048, thinking_mode: 'auto', reasoning_effort: 'medium' }],
  ['バランス', { temperature: 0.7, top_p: 0.9, top_k: 40, seed: null, max_tokens: 2048, thinking_mode: 'auto', reasoning_effort: 'medium' }],
  ['創造的', { temperature: 1.0, top_p: 0.95, top_k: 60, min_p: 0.02, seed: null, max_tokens: 3072, thinking_mode: 'auto', reasoning_effort: 'low' }],
  ['深い推論', { temperature: 0.5, top_p: 0.9, top_k: 40, max_tokens: 4096, thinking_mode: 'enabled', reasoning_effort: 'high' }],
  ['高速', { temperature: 0.4, top_p: 0.9, max_tokens: 1024, thinking_mode: 'disabled', reasoning_effort: 'low' }],
];
export function ParamGrid({ values, onChange, prefix, featureId }) {
  const [showAdv, setShowAdv] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testRes, setTestRes] = useState(null);
  const [testing, setTesting] = useState(false);
  const loadPreview = async () => {
    try { setPreview((await axios.post(`${API}/effective-preview`, { feature: featureId }, { withCredentials: true })).data); }
    catch (_e) { toast.error('プレビューの取得に失敗しました'); }
  };
  const runTest = async () => {
    setTesting(true); setTestRes(null);
    try { setTestRes((await axios.post(`${API}/test-config`, { feature: featureId }, { withCredentials: true })).data); }
    catch (_e) { toast.error('テストの実行に失敗しました'); }
    finally { setTesting(false); }
  };
  const applyPreset = (name, vals) => {
    Object.entries(vals).forEach(([k, v]) => onChange(k, v));
    toast.success(`プリセット「${name}」を適用しました（保存前に個別編集できます）`);
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-semibold text-zinc-400">プリセット:</span>
        {PRESETS.map(([name, vals]) => (
          <button key={name} type="button" onClick={() => applyPreset(name, vals)}
            data-testid={`${prefix}-preset-${name}`}
            className="rounded-full border border-indigo-200 bg-indigo-50/60 px-2.5 py-0.5 text-[10.5px] font-semibold text-indigo-700 hover:bg-indigo-100">
            {name}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 sm:grid-cols-3">
        {PARAM_DEFS.map(([k, label, ph]) => (
          <NumField key={k} k={k} label={label} ph={ph} values={values} onChange={onChange} prefix={prefix} />
        ))}
      </div>
      {/* 思考・推論設定 */}
      <div>
        <p className="text-[10px] font-bold text-violet-600 mb-1">思考・推論設定（対応モデルのみ）</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 sm:grid-cols-3">
          <SelField k="thinking_mode" label="思考モード" values={values} onChange={onChange} prefix={prefix}
            opts={[['auto', '自動'], ['enabled', '有効'], ['disabled', '無効']]} />
          <SelField k="reasoning_effort" label="推論の深さ" values={values} onChange={onChange} prefix={prefix}
            opts={[['low', '低'], ['medium', '中'], ['high', '高']]} />
        </div>
      </div>
      {/* 停止・出力制御 */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 sm:grid-cols-3">
        <SelField k="response_format" label="出力形式" values={values} onChange={onChange} prefix={prefix}
          opts={[['text', 'テキスト'], ['json_object', 'JSONオブジェクト']]} />
        <div className="col-span-2 min-w-0 sm:col-span-2">
          <label className="mb-0.5 block truncate text-[10px] font-semibold leading-tight text-zinc-500" title="停止シーケンス（カンマ区切り・最大4件）">停止シーケンス（カンマ区切り・最大4件）</label>
          <Input value={Array.isArray(values.stop) ? values.stop.join(', ') : (values.stop ?? '')}
            placeholder="例: ###, END"
            onChange={(e) => {
              const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 4);
              onChange('stop', e.target.value.trim() === '' ? null : arr);
            }}
            className="h-8 bg-white text-xs" data-testid={`${prefix}-stop`} />
        </div>
      </div>
      {/* 高度な互換性設定（折りたたみ） */}
      <button type="button" onClick={() => setShowAdv((v) => !v)}
        className="text-[10.5px] font-semibold text-zinc-500 hover:text-zinc-700"
        data-testid={`${prefix}-adv-toggle`}>
        {showAdv ? '▾' : '▸'} 高度な互換性設定（ローカルLLM専用サンプラー等）
      </button>
      {showAdv && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 sm:grid-cols-3 rounded-lg border border-zinc-100 bg-zinc-50/50 p-2.5">
          <SelField k="mirostat" label="Mirostat" values={values} onChange={onChange} prefix={prefix}
            opts={[[0, 'オフ'], [1, 'v1'], [2, 'v2']]} />
          {ADV_DEFS.map(([k, label, ph]) => (
            <NumField key={k} k={k} label={label} ph={ph} values={values} onChange={onChange} prefix={prefix} />
          ))}
          <p className="col-span-full text-[10px] text-zinc-400">
            クラウドモデルへは未対応パラメータを送信しません（自動除外）。空欄 = 継承。0 は明示的な値として送信されます。
          </p>
        </div>
      )}
      {/* 実際に適用される設定 + テスト */}
      {featureId && (
        <div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={loadPreview}
              className="inline-flex h-8 items-center justify-center gap-1 truncate rounded-lg border border-violet-200 bg-violet-50/60 px-2 text-[11px] font-semibold text-violet-700 hover:bg-violet-100"
              data-testid={`${prefix}-preview-btn`}>
              適用される設定を表示
            </button>
            <button type="button" onClick={runTest} disabled={testing}
              className="inline-flex h-8 items-center justify-center gap-1 truncate rounded-lg border border-emerald-200 bg-emerald-50/60 px-2 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              data-testid={`${prefix}-test-btn`}>
              {testing ? '⏳ テスト実行中…' : 'この設定でテスト実行'}
            </button>
          </div>
          {testRes && (
            <div className={`mt-1.5 rounded-lg border p-2.5 text-[10.5px] space-y-1 ${testRes.ok ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`}
              data-testid={`${prefix}-test-result`}>
              <p className={testRes.ok ? 'text-emerald-700 font-semibold' : 'text-rose-600 font-semibold'}>
                {testRes.ok ? `✓ 成功 — ${testRes.latency_ms}ms` : `✕ 失敗 — ${testRes.error || ''}（${testRes.latency_ms}ms）`}
              </p>
              <p className="text-zinc-600">モデル: <span className="font-mono">{testRes.model || '—'}</span>
                {testRes.ok && testRes.reply ? ` ／ 応答: ${testRes.reply.slice(0, 60)}` : ''}</p>
              <p className="text-zinc-500">送信パラメータ: <span className="font-mono">{Object.keys(testRes.params_sent || {}).join(', ') || '—'}</span></p>
              {(testRes.omitted || []).length > 0 && (
                <p className="text-amber-600">除外: {testRes.omitted.map((o) => o.param).join(', ')}</p>
              )}
            </div>
          )}
          {preview && (
            <div className="mt-1.5 rounded-lg border border-violet-100 bg-violet-50/40 p-2.5 text-[10.5px] space-y-1"
              data-testid={`${prefix}-preview`}>
              <p className="text-zinc-600">モデル: <span className="font-mono">{preview.model || '—'}</span> ／ エンドポイント: <span className="font-mono">{preview.endpoint}</span></p>
              {preview.note && <p className="text-amber-600">{preview.note}</p>}
              <pre className="whitespace-pre-wrap font-mono text-[10px] text-zinc-700">{JSON.stringify(preview.params, null, 1)}</pre>
              {(preview.omitted || []).length > 0 && (
                <p className="text-amber-600">除外: {preview.omitted.map((o) => `${o.param}（${o.reason}）`).join(' / ')}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 機能ごとのモデル選択 ─────────────────────────────────────────────
// Hoisted out of RoutingSection so React can keep the inner <Select>
// mounted across renders (fixes react/no-unstable-nested-components).
function ModelSelect({ fid, kind, field, placeholder, value, models, optionsFor, onChange }) {
  const isCurrentOffline = !!value && !!models.find((m) => m.id === value && m.online === false);
  const opts = optionsFor(kind, value);
  const unknownCurrent = !!value && value !== '__default__' && !opts.some((m) => m.id === value);
  return (
    <div className="space-y-1">
      <Select value={value || '__default__'} onValueChange={onChange}>
        <SelectTrigger className={`h-9 bg-white text-xs ${isCurrentOffline ? 'border-rose-300 ring-1 ring-rose-200' : ''}`}
          data-testid={`ai-route-${fid}-${field}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value="__default__">{placeholder}</SelectItem>
          {unknownCurrent && (
            <SelectItem value={value}>
              {value}
              <span className="ml-1 text-[10px] text-rose-500">(未検出)</span>
            </SelectItem>
          )}
          {opts.map((m) => (
            <SelectItem key={m.id} value={m.id}>
              {m.id}
              {m.online === false && <span className="ml-1 text-[10px] text-rose-500">(オフライン)</span>}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isCurrentOffline && (
        <p className="text-[10px] font-semibold text-rose-600">
          ⚠ このモデルはオフラインです — LM Studio でロードするか、別のモデルを選択してください
        </p>
      )}
    </div>
  );
}


// ── 推論プリセット管理（機能ルーティング配下） ───────────────────────────────
const PRESET_PARAM_FIELDS = [
  ['temperature', '温度', 0.05], ['top_p', 'Top P', 0.05], ['top_k', 'Top K', 1],
  ['repeat_penalty', '反復ペナルティ', 0.05], ['max_tokens', '最大出力トークン', 128],
];

function PresetManagerCard() {
  const [presets, setPresets] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, name_ja, description_ja, feature_scope, params, enabled}
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${RUNTIME_API}/admin/presets`)
    .then((r) => setPresets(r.data.presets || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const savePreset = async () => {
    if (!edit?.name_ja?.trim()) { toast.error('プリセット名（日本語）は必須です'); return; }
    setBusy(true);
    try {
      const body = {
        name_ja: edit.name_ja, name_en: edit.name_en || '',
        description_ja: edit.description_ja || '', description_en: edit.description_en || '',
        feature_scope: edit.feature_scope || [], enabled: edit.enabled !== false,
        params: Object.fromEntries(Object.entries(edit.params || {})
          .filter(([, v]) => v !== '' && v !== null && v !== undefined)
          .map(([k, v]) => [k, Number(v)])),
      };
      if (edit.id) await axios.patch(`${RUNTIME_API}/admin/presets/${edit.id}`, body);
      else await axios.post(`${RUNTIME_API}/admin/presets`, body);
      toast.success('プリセットを保存しました');
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  const toggle = async (p) => {
    try {
      await axios.patch(`${RUNTIME_API}/admin/presets/${p.id}`, {
        name_ja: p.name_ja, name_en: p.name_en || '', description_ja: p.description_ja || '',
        description_en: p.description_en || '', feature_scope: p.feature_scope || [],
        params: p.params || {}, enabled: !(p.enabled !== false),
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '更新に失敗しました'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`プリセット「${p.name_ja}」を削除しますか？`)) return;
    try { await axios.delete(`${RUNTIME_API}/admin/presets/${p.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  return (
    <Card title="推論プリセット" testid="ai-preset-manager"
      hint="ユーザーがチャット・日報AIで選択できる推論設定のセットです。プリセットは管理者の上限（トークン上限・コンテキスト長）を超えることはできません。"
      action={(
        <Button size="sm" variant="outline" className="h-8 text-xs"
          onClick={() => setEdit({ name_ja: '', description_ja: '', feature_scope: [], params: {}, enabled: true })}
          data-testid="ai-preset-new-btn">＋ 新規プリセット</Button>
      )}>
      <div className="divide-y divide-zinc-200 rounded-lg border border-zinc-200/60">
        {presets.map((p) => (
          <div key={p.id} className={`flex flex-wrap items-center gap-2 px-3 py-2 ${p.enabled === false ? 'opacity-50' : ''}`}
            data-testid={`ai-preset-row-${p.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-zinc-800">
                {p.name_ja}
                {p.is_system && <span className="ml-1.5 rounded border border-zinc-200 bg-zinc-50 px-1 text-[9px] font-bold text-zinc-500">システム</span>}
                {(p.feature_scope || []).length > 0 && <span className="ml-1.5 text-[9px] text-indigo-500">{(p.feature_scope || []).join(', ')}</span>}
              </p>
              <p className="text-[10px] text-zinc-400 truncate">
                {p.description_ja}
                <span className="ml-1 font-mono text-zinc-300">
                  {Object.entries(p.params || {}).map(([k, v]) => `${k}=${v}`).join(' ')}
                </span>
              </p>
            </div>
            <button type="button" onClick={() => toggle(p)} data-testid={`ai-preset-toggle-${p.id}`}
              className={`rounded border px-2 py-0.5 text-[10px] font-bold ${p.enabled === false ? 'border-zinc-200 text-zinc-400' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
              {p.enabled === false ? '無効' : '有効'}
            </button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setEdit({ ...p })}
              data-testid={`ai-preset-edit-${p.id}`}>編集</Button>
            {!p.is_system && (
              <Button size="sm" variant="ghost" className="h-7 text-[10px] text-rose-600" onClick={() => remove(p)}
                data-testid={`ai-preset-delete-${p.id}`}>削除</Button>
            )}
          </div>
        ))}
        {presets.length === 0 && <p className="px-3 py-4 text-center text-[11px] text-zinc-400">プリセットがありません</p>}
      </div>

      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:w-[480px]" data-testid="ai-preset-sheet">
          {edit && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">{edit.id ? 'プリセットを編集' : '新規プリセット'}</SheetTitle>
                <SheetDescription className="text-xs">空欄のパラメータは機能・グローバル既定値を使用します。</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                {[['name_ja', 'プリセット名（日本語）'], ['name_en', 'プリセット名（英語）'],
                  ['description_ja', '説明（日本語）'], ['description_en', '説明（英語）']].map(([k, label]) => (
                  <div key={k}>
                    <label className="text-[10px] font-semibold text-zinc-500">{label}</label>
                    <Input value={edit[k] || ''} onChange={(e) => setEdit((x) => ({ ...x, [k]: e.target.value }))}
                      className="h-8 text-xs" data-testid={`ai-preset-field-${k}`} />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] font-semibold text-zinc-500">対象機能</label>
                  <Select value={(edit.feature_scope || [])[0] || '__all__'}
                    onValueChange={(v) => setEdit((x) => ({ ...x, feature_scope: v === '__all__' ? [] : [v] }))}>
                    <SelectTrigger className="h-8 bg-white text-xs" data-testid="ai-preset-scope"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__all__">すべての機能</SelectItem>
                      <SelectItem value="sensbey_chat_assistant">Sensbey AIアシスタント</SelectItem>
                      <SelectItem value="daily_report_drafting">日報下書き作成</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_PARAM_FIELDS.map(([k, label, step]) => (
                    <div key={k}>
                      <label className="text-[10px] font-semibold text-zinc-500">{label}</label>
                      <Input type="number" step={step} value={edit.params?.[k] ?? ''}
                        onChange={(e) => setEdit((x) => ({ ...x, params: { ...(x.params || {}), [k]: e.target.value === '' ? '' : e.target.value } }))}
                        className="h-8 text-xs" data-testid={`ai-preset-param-${k}`} />
                    </div>
                  ))}
                </div>
                <Button onClick={savePreset} disabled={busy}
                  className="w-full bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-preset-save-btn">
                  {busy ? '保存中…' : '保存'}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  );
}

// ── 機能ルーティング（サマリーテーブル + Sheet編集） ─────────────────────────
export function RoutingSection({ features, settings, models, save, saving }) {
  const [routing, setRouting] = useState(settings.routing || {});
  const [overrides, setOverrides] = useState(settings.inference_overrides || {});
  const [limits, setLimits] = useState(settings.token_limits || {});
  const [defaults, setDefaults] = useState(settings.inference_defaults || {});
  const [caps, setCaps] = useState(settings.model_capabilities || {});
  const [editFid, setEditFid] = useState(null);
  const [showOffline, setShowOffline] = useState(false);
  const [presetOptions, setPresetOptions] = useState([]);
  useEffect(() => {
    axios.get(`${RUNTIME_API}/admin/presets`)
      .then((r) => setPresetOptions((r.data.presets || []).filter((p) => p.enabled !== false)))
      .catch(() => {});
  }, []);
  const feat = features.find((f) => f.id === editFid);
  // Only offer models that LM Studio currently reports as online. Offline
  // entries can be resurfaced with a toggle — but the default keeps admins
  // out of the "stuck at step 1/4" trap where a picked model isn't loaded.
  const optionsFor = (kind, currentValue) => {
    const typeMatch = (m) => kind === 'vision' ? m.type === 'vision'
      : kind === 'embedding' ? m.type === 'embedding'
      : m.type !== 'embedding';
    const isOnline = (m) => m.online !== false; // treat missing flag as online
    return models.filter((m) => {
      if (!typeMatch(m)) return false;
      if (m.id === currentValue) return true; // never hide the currently-saved value
      if (showOffline) return true;
      return isOnline(m);
    });
  };
  const setRoute = (fid, key, v) => setRouting((r) => ({
    ...r, [fid]: { model: '', fallback_model: '', ...(r[fid] || {}), [key]: v === '__default__' ? '' : v } }));

  const saveAll = async () => {
    const ok = await save({ routing, inference_overrides: overrides, token_limits: limits, inference_defaults: defaults });
    if (ok) setEditFid(null);
  };

  const offlineIds = new Set(models.filter((m) => m.online === false).map((m) => m.id));
  const routesOnOffline = features.filter((f) => {
    const r = routing[f.id] || {};
    return offlineIds.has(r.model) || offlineIds.has(r.fallback_model);
  });

  return (
    <div className="space-y-4">
      <DocReadCard />
      {routesOnOffline.length > 0 && (
        <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-4" data-testid="ai-route-offline-warning">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-rose-700 font-bold text-sm">⚠ オフラインモデルに割り当てられた機能があります</span>
          </div>
          <p className="text-[11px] text-rose-800 mb-2 leading-snug">
            以下の機能は現在 LM Studio でロードされていないモデルを使用しています。
            AI応答がタイムアウトする原因になります — 該当機能の「設定」からロード済みモデルに切り替えてください。
          </p>
          <ul className="space-y-0.5 text-[11px] text-rose-800">
            {routesOnOffline.map((f) => {
              const r = routing[f.id] || {};
              return (
                <li key={f.id} className="flex items-center gap-2" data-testid={`ai-route-offline-${f.id}`}>
                  <span className="font-semibold">{f.label_ja}</span>
                  <span className="font-mono text-[10px] text-rose-600">{r.model || r.fallback_model}</span>
                  <button type="button" onClick={() => setEditFid(f.id)}
                    className="ml-auto text-[10px] font-semibold text-rose-700 underline">修正する</button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <Card title="機能別モデル割り当て" testid="ai-console-routing"
        hint="どの機能をどのモデルで動かすかの一覧です。各行の「設定」から、モデル・フォールバック・細かな推論調整・トークン上限をまとめて変更できます。グレーの行は将来の機能（実装待ち）です。"
        action={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer" title="オフライン中のモデルも選択肢に含めます">
              <input type="checkbox" checked={showOffline} onChange={(e) => setShowOffline(e.target.checked)}
                data-testid="ai-route-show-offline" className="h-3 w-3 accent-indigo-600" />
              オフラインを含める
            </label>
            <Button size="sm" onClick={saveAll} disabled={saving}
              className="h-9 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-route-save">
              {saving ? '保存中…' : 'すべて保存'}
            </Button>
          </div>
        }>
        <div className="overflow-hidden rounded-lg border border-zinc-200/60">
          {features.map((f, i) => {
            const r = routing[f.id] || {};
            const ovCount = Object.keys(overrides[f.id] || {}).filter((k) => overrides[f.id][k] != null).length;
            const lim = limits[f.id] || {};
            const modelOffline = r.model && offlineIds.has(r.model);
            return (
              <div key={f.id}
                className={`flex flex-wrap items-center gap-3 px-4 py-3 transition-colors duration-200 ${i > 0 ? 'border-t border-zinc-200' : ''} ${modelOffline ? 'bg-rose-50/60' : f.active ? 'hover:bg-teal-50/50' : 'bg-zinc-50/70 opacity-55'}`}
                data-testid={`ai-route-row-${f.id}`}>
                <CapBadge type={f.kind} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-semibold text-zinc-800">
                    {f.label_ja}
                    {!f.active && <span className="ml-2 rounded bg-zinc-200 px-1.5 py-0.5 text-[9px] font-bold text-zinc-500">将来</span>}
                    {modelOffline && <span className="ml-2 rounded bg-rose-100 border border-rose-300 px-1.5 py-0.5 text-[9px] font-bold text-rose-700">オフライン</span>}
                  </p>
                  <p className="mt-0.5 flex flex-wrap gap-x-3 text-[10.5px] text-zinc-400">
                    <span className={`font-mono ${modelOffline ? 'text-rose-600' : ''}`}>{r.model || '既定モデル'}</span>
                    {r.fallback_model && <span>予備: <span className="font-mono">{r.fallback_model}</span></span>}
                    {ovCount > 0 && <span className="text-indigo-500">調整 {ovCount} 項目</span>}
                    {(lim.max_input_tokens || lim.max_output_tokens) && (
                      <span className="text-amber-600">上限 {lim.max_input_tokens || '∞'}→{lim.max_output_tokens || '∞'}</span>)}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditFid(f.id)}
                  className="h-8 text-[11px]" data-testid={`ai-feature-edit-${f.id}`}>
                  <Settings2 size={12} strokeWidth={1.5} className="mr-1" /> 設定
                </Button>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="グローバル推論既定値" hint="機能個別の調整が無い場合に全機能へ適用される既定値です。通常は変更不要です。">
        <Accordion type="single" collapsible>
          <AccordionItem value="g" className="border-none">
            <AccordionTrigger className="rounded-lg border border-zinc-200/60 px-3 py-2 text-xs font-semibold text-zinc-600 hover:no-underline" data-testid="ai-inf-global-toggle">
              <span className="flex items-center gap-1.5"><SlidersHorizontal size={13} strokeWidth={1.5} /> 既定値を編集する</span>
            </AccordionTrigger>
            <AccordionContent className="pt-3">
              <ParamGrid values={defaults} onChange={(k, v) => setDefaults((d) => ({ ...d, [k]: v }))} prefix="ai-inf-default" />
              <div className="mt-3 flex justify-end">
                <Button size="sm" onClick={saveAll} disabled={saving} className="h-8 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-inf-save">保存</Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      {/* ── モデル能力レジストリ（思考対応の切替） ── */}
      <Card title="モデル能力（思考・推論対応）"
        hint="モデルごとに reasoning 対応を指定します。オンのモデルにのみ思考設定（reasoning_effort）がAPIリクエストで送信されます。">
        <div className="space-y-1" data-testid="ai-caps-table">
          {(showOffline ? models : models.filter((m) => m.online !== false)).map((m) => {
            const on = !!(caps[m.id] || {}).reasoning;
            return (
              <label key={m.id} className="flex items-center gap-2.5 rounded-lg border border-zinc-100 bg-white px-3 py-1.5 cursor-pointer hover:bg-zinc-50">
                <input type="checkbox" checked={on}
                  onChange={(e) => setCaps((c) => ({ ...c, [m.id]: { ...(c[m.id] || {}), reasoning: e.target.checked } }))}
                  data-testid={`ai-cap-reasoning-${m.id}`}
                  className="h-3.5 w-3.5 accent-violet-600" />
                <span className="font-mono text-[11px] text-zinc-700 truncate flex-1">{m.id}</span>
                <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${on ? 'bg-violet-100 text-violet-700' : 'bg-zinc-100 text-zinc-400'}`}>
                  {on ? '思考対応' : '未対応'}
                </span>
              </label>
            );
          })}
          {models.length === 0 && <p className="text-[11px] text-zinc-400">検出されたモデルがありません（「検出されたモデル」で更新してください）。</p>}
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={() => save({ model_capabilities: caps })} disabled={saving}
            className="h-8 bg-violet-600 text-xs hover:bg-violet-700" data-testid="ai-caps-save">能力を保存</Button>
        </div>
      </Card>

      <PresetManagerCard />

      {/* ── 機能ごとの詳細 Sheet ── */}
      <Sheet open={!!editFid} onOpenChange={(o) => !o && setEditFid(null)}>
        <SheetContent
          className="w-full overflow-y-auto bg-white sm:w-[min(640px,calc(100vw-32px))] sm:max-w-[min(640px,calc(100vw-32px))]"
          data-testid="ai-feature-sheet">
          {feat && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 text-base">
                  <CapBadge type={feat.kind} /> {feat.label_ja}
                </SheetTitle>
                <SheetDescription className="text-xs">この機能だけに適用される設定です。空欄はグローバル既定値を使用します。</SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-5">
                <div className="space-y-2.5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">使用モデル</p>
                  <ModelSelect fid={feat.id} kind={feat.kind} field="model" placeholder="既定（全般タブのモデル）"
                    value={(routing[feat.id] || {}).model || ''} models={models} optionsFor={optionsFor}
                    onChange={(v) => setRoute(feat.id, 'model', v)} />
                  {feat.kind !== 'embedding' && (
                    <>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">フォールバック（障害時の予備）</p>
                      <ModelSelect fid={feat.id} kind={feat.kind} field="fallback_model" placeholder="フォールバックなし"
                        value={(routing[feat.id] || {}).fallback_model || ''} models={models} optionsFor={optionsFor}
                        onChange={(v) => setRoute(feat.id, 'fallback_model', v)} />
                    </>
                  )}
                  {['sensbey_chat_assistant', 'daily_report_drafting'].includes(feat.id) && (
                    <>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">既定プリセット（ユーザー未選択時）</p>
                      <Select value={(routing[feat.id] || {}).default_preset || '__default__'}
                        onValueChange={(v) => setRoute(feat.id, 'default_preset', v)}>
                        <SelectTrigger className="h-9 bg-white text-xs" data-testid={`ai-route-${feat.id}-default-preset`}>
                          <SelectValue placeholder="プリセットなし" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="__default__">プリセットなし（推論パラメータのみ）</SelectItem>
                          {presetOptions.filter((p) => !(p.feature_scope || []).length || (p.feature_scope || []).includes(feat.id)).map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name_ja}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-zinc-400">ユーザーはチャット・日報AIの画面から自分専用のモデル/プリセットを選べます（この既定値は変更されません）。</p>
                    </>
                  )}
                </div>
                {feat.kind !== 'embedding' && (
                  <Accordion type="multiple" className="space-y-2">
                    <AccordionItem value="inf" className="rounded-lg border border-zinc-200/60 px-3">
                      <AccordionTrigger className="py-2.5 text-xs font-semibold text-zinc-600 hover:no-underline" data-testid="ai-feature-sheet-inf">
                        <span className="flex items-center gap-1.5"><SlidersHorizontal size={13} strokeWidth={1.5} /> 推論パラメータの調整</span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-3">
                        <ParamGrid values={overrides[feat.id] || {}} prefix={`ai-inf-${feat.id}`} featureId={feat.id}
                          onChange={(k, v) => setOverrides((o) => ({ ...o, [feat.id]: { ...(o[feat.id] || {}), [k]: v } }))} />
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="lim" className="rounded-lg border border-zinc-200/60 px-3">
                      <AccordionTrigger className="py-2.5 text-xs font-semibold text-zinc-600 hover:no-underline" data-testid="ai-feature-sheet-lim">
                        <span className="flex items-center gap-1.5"><Gauge size={13} strokeWidth={1.5} /> トークン上限（コスト・暴走防止）</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-2 pb-3">
                        {[['max_input_tokens', '入力上限（トークン）', 'ai-limit-' + feat.id + '-in'],
                          ['max_output_tokens', '出力上限（トークン）', 'ai-limit-' + feat.id + '-out']].map(([k, label, tid]) => (
                          <div key={k}>
                            <label className="text-[10px] font-semibold text-zinc-500">{label}</label>
                            <Input type="number" placeholder="無制限" value={(limits[feat.id] || {})[k] ?? ''}
                              onChange={(e) => setLimits((l) => ({ ...l, [feat.id]: { ...(l[feat.id] || {}), [k]: e.target.value === '' ? null : Number(e.target.value) } }))}
                              className="h-8 bg-white text-xs" data-testid={tid} />
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
                <Button onClick={saveAll} disabled={saving}
                  className="w-full bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-feature-sheet-save">
                  {saving ? '保存中…' : 'この設定を保存'}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
